import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlowGraphData, FlowNode, FlowEdge } from '../types/protocol';

interface FlowPreviewProps {
  data: FlowGraphData | null;
  label?: string;
}

/* ── Dark theme (matches ACD IVR builder) ──────────────────── */
const TH = {
  canvasBg: '#13132b',
  nodeBg: '#252542',
  nodeBorder: '#3a3a58',
  text: '#e0e0f0',
  textDim: '#8888aa',
  textMuted: '#6c6c88',
  edge: '#4a4a6a',
  port: '#5a5a7a',
  footerDiv: '#3a3a58',
  selectStroke: '#7c8cf8',
};

/* ── Node-type registry (ACD IVR + Scripter flow types) ──── */
interface NStyle { accent: string; letter: string; typeLabel: string }
const NS: Record<string, NStyle> = {
  // ACD IVR node types
  A: { accent: '#4caf50', letter: 'A', typeLabel: 'ANSWER' },
  P: { accent: '#66bb6a', letter: 'P', typeLabel: 'PLAY' },
  K: { accent: '#ffa726', letter: 'K', typeLabel: 'MENU' },
  Q: { accent: '#ef5350', letter: 'Q', typeLabel: 'QUEUE' },
  S: { accent: '#ab47bc', letter: 'S', typeLabel: 'SCHEDULE' },
  D: { accent: '#42a5f5', letter: 'D', typeLabel: 'DECISION' },
  G: { accent: '#26a69a', letter: 'G', typeLabel: 'GATHER DIGITS' },
  T: { accent: '#26a69a', letter: 'T', typeLabel: 'DTMF' },
  J: { accent: '#7e57c2', letter: 'J', typeLabel: 'JUMP' },
  L: { accent: '#7e57c2', letter: 'L', typeLabel: 'LOOP' },
  R: { accent: '#5c6bc0', letter: 'R', typeLabel: 'DESTINATION' },
  H: { accent: '#e53935', letter: 'H', typeLabel: 'HANG UP' },
  E: { accent: '#7cb342', letter: 'E', typeLabel: 'VALUE STORE' },
  F: { accent: '#7cb342', letter: 'F', typeLabel: 'VALUE FETCH' },
  V: { accent: '#00acc1', letter: 'V', typeLabel: 'GATHER VOICE' },
  M: { accent: '#ab47bc', letter: 'M', typeLabel: 'SPEECH TO TEXT' },
  I: { accent: '#78909c', letter: 'I', typeLabel: 'SINGLETRACK' },
  Y: { accent: '#00acc1', letter: 'Y', typeLabel: 'READBACK' },
  Z: { accent: '#fdd835', letter: 'Z', typeLabel: 'SILENT PAY' },
  C: { accent: '#607d8b', letter: 'C', typeLabel: 'CUSTOM' },
  X: { accent: '#607d8b', letter: 'X', typeLabel: 'XML' },
  W: { accent: '#78909c', letter: 'W', typeLabel: 'WAIT FOR SILENCE' },
  // Scripter flow node types — colors match Scripter UI (green/gold/red)
  'source/embed': { accent: '#4caf50', letter: 'E', typeLabel: 'EMBED' },
  'handler/form': { accent: '#f0c040', letter: 'F', typeLabel: 'FORM' },
  'handler/schedule': { accent: '#3498db', letter: 'S', typeLabel: 'SCHEDULE' },
  'handler/split': { accent: '#9b59b6', letter: 'T', typeLabel: 'TEST' },
  'target/integration': { accent: '#e74c3c', letter: 'I', typeLabel: 'INTEGRATION' },
};
const DS: NStyle = { accent: '#78909c', letter: '?', typeLabel: 'NODE' };
function nst(type: string): NStyle { return NS[type] ?? NS[type.charAt(0)] ?? DS; }

/* ── Adaptive sizing ───────────────────────────────────────── */
interface Dims { w: number; h: number; hGap: number; vGap: number; fontSize: number }
const PORT_R = 4;

function getDims(n: number): Dims {
  if (n <= 5)  return { w: 180, h: 68, hGap: 45, vGap: 65, fontSize: 11 };
  if (n <= 10) return { w: 160, h: 62, hGap: 38, vGap: 55, fontSize: 10.5 };
  if (n <= 20) return { w: 145, h: 56, hGap: 30, vGap: 48, fontSize: 10 };
  return { w: 130, h: 50, hGap: 25, vGap: 42, fontSize: 9 };
}

/* ── Hierarchical tree layout ───────────────────────────────── */
function layoutNodes(
  nodes: FlowNode[], edges: FlowEdge[], d: Dims,
): Map<string, { x: number; y: number }> {
  const pos = new Map<string, { x: number; y: number }>();
  if (nodes.length === 0) return pos;

  // 1. Build forward adjacency (preserving edge order for menu options)
  const fwd = new Map<string, string[]>();
  const hasIncoming = new Set<string>();
  for (const e of edges) {
    let list = fwd.get(e.from);
    if (!list) { list = []; fwd.set(e.from, list); }
    if (!list.includes(e.to)) list.push(e.to);
    hasIncoming.add(e.to);
  }

  // 2. BFS depth assignment (cycle-safe)
  const depth = new Map<string, number>();
  const roots = nodes.filter(n => !hasIncoming.has(n.id));
  if (roots.length === 0) roots.push(nodes[0]!);
  const queue = roots.map(r => r.id);
  for (const id of queue) depth.set(id, 0);
  let qi = 0;
  while (qi < queue.length) {
    const id = queue[qi++]!;
    const d0 = depth.get(id)!;
    for (const ch of fwd.get(id) ?? []) {
      if (!depth.has(ch)) {
        depth.set(ch, d0 + 1);
        queue.push(ch);
      }
    }
  }
  // Handle unvisited nodes (cycles)
  for (const n of nodes) {
    if (!depth.has(n.id)) {
      depth.set(n.id, 0);
      queue.push(n.id);
    }
  }

  // 2b. Push shared nodes below ALL parents (single pass in BFS order)
  const bfsDepth = new Map(depth);
  const rev = new Map<string, string[]>();
  for (const e of edges) {
    if (e.from === e.to) continue; // skip self-loops
    let list = rev.get(e.to);
    if (!list) { list = []; rev.set(e.to, list); }
    if (!list.includes(e.from)) list.push(e.from);
  }
  for (const id of queue) {
    const parents = rev.get(id);
    if (!parents) continue;
    // Only consider forward-edge parents (BFS depth <= this node's BFS depth)
    const fwdParents = parents.filter(p => (bfsDepth.get(p) ?? 0) <= (bfsDepth.get(id) ?? 0));
    if (fwdParents.length === 0) continue;
    const maxParentD = Math.max(...fwdParents.map(p => depth.get(p) ?? 0));
    if ((depth.get(id) ?? 0) <= maxParentD) {
      depth.set(id, maxParentD + 1);
    }
  }

  // 3. Build layout tree — each child claimed by first parent (in BFS order)
  const layoutKids = new Map<string, string[]>();
  const claimed = new Set<string>();
  for (const id of queue) {
    const kids: string[] = [];
    for (const ch of fwd.get(id) ?? []) {
      if (!claimed.has(ch)) {
        claimed.add(ch);
        kids.push(ch);
      }
    }
    if (kids.length > 0) layoutKids.set(id, kids);
  }

  // 4. Subtree widths (bottom-up, in units of nodes)
  const stw = new Map<string, number>();
  const calcW = (id: string, seen: Set<string>): number => {
    if (seen.has(id)) return 1;
    seen.add(id);
    const cached = stw.get(id);
    if (cached !== undefined) return cached;
    const kids = layoutKids.get(id);
    if (!kids || kids.length === 0) { stw.set(id, 1); return 1; }
    let w = 0;
    for (const k of kids) w += calcW(k, seen);
    stw.set(id, w);
    return w;
  };
  const guard = new Set<string>();
  for (const id of queue) calcW(id, guard);

  // 5. Position top-down — children centered below parent
  const unit = d.w + d.hGap;
  const place = (id: string, cx: number, ly: number) => {
    pos.set(id, { x: cx, y: ly * (d.h + d.vGap) + d.h / 2 });
    const kids = layoutKids.get(id);
    if (!kids || kids.length === 0) return;
    let totalW = 0;
    for (const k of kids) totalW += (stw.get(k) ?? 1);
    let left = cx - (totalW * unit) / 2;
    for (const k of kids) {
      const kw = (stw.get(k) ?? 1) * unit;
      place(k, left + kw / 2, (depth.get(k) ?? ly + 1));
      left += kw;
    }
  };

  // 6. Position roots side-by-side
  let totalRootW = 0;
  for (const r of roots) totalRootW += (stw.get(r.id) ?? 1);
  let rootLeft = -(totalRootW * unit) / 2;
  for (const r of roots) {
    const rw = (stw.get(r.id) ?? 1) * unit;
    place(r.id, rootLeft + rw / 2, 0);
    rootLeft += rw;
  }

  // 7. Reposition shared nodes — center between forward-edge parents only
  for (const id of queue) {
    const parents = rev.get(id);
    if (!parents || parents.length <= 1) continue;
    // Only consider parents that aren't back-edges
    const fwdParents = parents.filter(p => (bfsDepth.get(id) ?? 0) >= (bfsDepth.get(p) ?? 0));
    const parentXs = fwdParents.map(p => pos.get(p)?.x).filter((x): x is number => x !== undefined);
    if (parentXs.length <= 1) continue;
    const avgX = parentXs.reduce((a, b) => a + b, 0) / parentXs.length;
    const oldX = pos.get(id)?.x;
    if (oldX === undefined) continue;
    const dx = avgX - oldX;
    if (Math.abs(dx) < 1) continue;
    // Shift this node and its owned subtree
    const shift = (nid: string) => {
      const p = pos.get(nid);
      if (p) p.x += dx;
      for (const ch of layoutKids.get(nid) ?? []) shift(ch);
    };
    shift(id);
  }

  // 8. Resolve overlaps per layer
  const layers = new Map<number, string[]>();
  for (const n of nodes) {
    const d0 = depth.get(n.id) ?? 0;
    let layer = layers.get(d0);
    if (!layer) { layer = []; layers.set(d0, layer); }
    layer.push(n.id);
  }
  const minGap = d.w + d.hGap;
  for (const [, layer] of layers) {
    layer.sort((a, b) => (pos.get(a)?.x ?? 0) - (pos.get(b)?.x ?? 0));
    for (let i = 1; i < layer.length; i++) {
      const prev = pos.get(layer[i - 1]!);
      const curr = pos.get(layer[i]!);
      if (!prev || !curr) continue;
      const overlap = (prev.x + minGap / 2) - (curr.x - minGap / 2);
      if (overlap > 0) {
        // Push current (and its subtree) right
        const shiftRight = (nid: string) => {
          const p = pos.get(nid);
          if (p) p.x += overlap;
          for (const ch of layoutKids.get(nid) ?? []) shiftRight(ch);
        };
        shiftRight(layer[i]!);
      }
    }
  }

  // 9. Fallback — any unpositioned nodes
  for (const n of nodes) {
    if (!pos.has(n.id)) {
      pos.set(n.id, { x: 0, y: (depth.get(n.id) ?? 0) * (d.h + d.vGap) + d.h / 2 });
    }
  }

  return pos;
}

/* ── Port X position for multi-output nodes ─────────────────── */
function pX(cx: number, w: number, idx: number, total: number): number {
  if (total <= 1) return cx;
  const usable = w * 0.65;
  const start = cx - usable / 2;
  return start + (idx / (total - 1)) * usable;
}

/* ── Bezier edge path ────────────────────────────────────────── */
function bez(fx: number, fy: number, tx: number, ty: number): string {
  const dy = Math.abs(ty - fy);
  const c = Math.max(30, dy * 0.35);
  return `M${fx},${fy} C${fx},${fy + c} ${tx},${ty - c} ${tx},${ty}`;
}

/* ── Node card ─────────────────────────────────────────────── */
function NodeCard({
  node, cx, cy, dims: d, outputs, selected, onClick,
}: {
  node: FlowNode; cx: number; cy: number; dims: Dims;
  outputs: { to: string; label: string }[];
  selected: boolean; onClick: () => void;
}): React.ReactElement {
  const st = nst(node.type);
  const lx = cx - d.w / 2;
  const ly = cy - d.h / 2;
  const footerH = outputs.length > 0 ? Math.round(d.h * 0.3) : 0;
  const headerH = d.h - footerH;
  const icoSz = Math.round(d.fontSize * 1.35);
  const maxCh = Math.floor((d.w - icoSz - 42) / (d.fontSize * 0.58));
  const dLabel = node.label.length > maxCh ? node.label.slice(0, maxCh - 1) + '\u2026' : node.label;

  const hdrH = Math.round(d.h * 0.32);

  return (
    <g className="sp-flow-node-card" onClick={(e) => { e.stopPropagation(); onClick(); }}>
      {/* Card background */}
      <rect x={lx} y={ly} width={d.w} height={d.h} rx={6}
        fill={TH.nodeBg} stroke={selected ? TH.selectStroke : TH.nodeBorder}
        strokeWidth={selected ? 2 : 1} />

      {/* Colored header bar (matches Scripter: green/gold/red) */}
      <clipPath id={`hdr-${node.id}`}>
        <rect x={lx} y={ly} width={d.w} height={hdrH} rx={6} />
      </clipPath>
      <rect x={lx} y={ly} width={d.w} height={hdrH + 6}
        fill={st.accent} clipPath={`url(#hdr-${node.id})`} />

      {/* Icon letter in header */}
      <text x={lx + 10} y={ly + hdrH / 2}
        fontSize={d.fontSize} fontWeight="700" fill="#fff"
        dominantBaseline="central">
        {st.letter}
      </text>

      {/* Node name in header */}
      <text x={lx + 10 + d.fontSize + 4} y={ly + hdrH / 2}
        fontSize={d.fontSize} fontWeight="600" fill="#fff"
        dominantBaseline="central">
        {dLabel}
      </text>

      {/* Type label below header */}
      <text x={lx + 16 + icoSz + 6} y={ly + 12 + icoSz + d.fontSize * 0.75}
        fontSize={d.fontSize - 2} fontWeight="500" fill={TH.textDim}
        letterSpacing="0.5">
        {st.typeLabel}
      </text>

      {/* Footer divider + output labels */}
      {outputs.length > 0 && (
        <>
          <line x1={lx + 6} y1={ly + headerH} x2={lx + d.w - 6} y2={ly + headerH}
            stroke={TH.footerDiv} strokeWidth={0.5} />
          {outputs.map((out, i) => {
            const px = pX(cx, d.w, i, outputs.length);
            return (
              <text key={i} x={px} y={ly + headerH + footerH / 2 + 1}
                fontSize={d.fontSize - 2} fontWeight="600" fill={TH.textMuted}
                textAnchor="middle" dominantBaseline="central">
                {out.label.toUpperCase()}
              </text>
            );
          })}
          {/* Vertical dividers between outputs */}
          {outputs.length > 1 && outputs.slice(0, -1).map((_, i) => {
            const p1 = pX(cx, d.w, i, outputs.length);
            const p2 = pX(cx, d.w, i + 1, outputs.length);
            return (
              <line key={`d${i}`}
                x1={(p1 + p2) / 2} y1={ly + headerH + 4}
                x2={(p1 + p2) / 2} y2={ly + d.h - 4}
                stroke={TH.footerDiv} strokeWidth={0.5} />
            );
          })}
        </>
      )}

      {/* Input port (top center) */}
      <circle cx={cx} cy={ly} r={PORT_R} fill={TH.port} />

      {/* Output ports (bottom) */}
      {outputs.length > 0
        ? outputs.map((_, i) => (
            <circle key={`p${i}`}
              cx={pX(cx, d.w, i, outputs.length)}
              cy={ly + d.h} r={PORT_R} fill={st.accent} />
          ))
        : <circle cx={cx} cy={ly + d.h} r={PORT_R} fill={TH.port} />
      }

      {/* Selection glow */}
      {selected && (
        <rect x={lx - 3} y={ly - 3} width={d.w + 6} height={d.h + 6} rx={9}
          fill="none" stroke={TH.selectStroke} strokeWidth={1.5} opacity={0.5} />
      )}
    </g>
  );
}

/* ── Edge line (dashed bezier) ─────────────────────────────── */
function EdgeLine({ from, to }: {
  from: { x: number; y: number }; to: { x: number; y: number };
}): React.ReactElement {
  return (
    <path d={bez(from.x, from.y, to.x, to.y)}
      fill="none" stroke={TH.edge} strokeWidth={1.5} strokeDasharray="6 3"
      markerEnd="url(#sp-flow-arrow)" />
  );
}

/* ── Detail panel (shown when a node is clicked) ───────────── */
function DetailPanel({ node, outgoing, incoming, nodeMap, onClose }: {
  node: FlowNode;
  outgoing: { to: string; label: string }[];
  incoming: { from: string; label: string }[];
  nodeMap: Map<string, FlowNode>;
  onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const st = nst(node.type);
  const config = node.config ?? {};
  const configEntries = Object.entries(config).filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <div className="sp-flow-detail" style={{ borderLeftColor: st.accent }}>
      <div className="sp-flow-detail-header">
        <span className="sp-flow-detail-icon" style={{ background: st.accent + '2a', color: st.accent }}>
          {st.letter}
        </span>
        <strong className="sp-flow-detail-name">{node.label}</strong>
        <span className="sp-flow-detail-badge" style={{ borderColor: st.accent, color: st.accent }}>
          {st.typeLabel}
        </span>
        <button className="sp-flow-detail-close" onClick={onClose} aria-label="Close">{'\u2715'}</button>
      </div>
      <div className="sp-flow-detail-body">
        {configEntries.length > 0 && (
          <div className="sp-flow-detail-config">
            {configEntries.map(([key, val]) => (
              <span key={key} className="sp-flow-detail-config-item">
                <span className="sp-flow-detail-config-key">{key}</span>
                <span className="sp-flow-detail-config-val">{typeof val === 'boolean' ? (val ? t('fields.yes') : t('fields.no')) : String(val)}</span>
              </span>
            ))}
          </div>
        )}
        {outgoing.length > 0 && (
          <div className="sp-flow-detail-conns">
            <span className="sp-flow-detail-sect">{t('flow.outgoing')}</span>
            {outgoing.map((e, i) => {
              const target = nodeMap.get(e.to);
              return (
                <span key={i} className="sp-flow-detail-conn">
                  {'\u2192'} {target?.label ?? e.to}
                  <span className="sp-flow-detail-conn-via">{e.label.toUpperCase()}</span>
                </span>
              );
            })}
          </div>
        )}
        {incoming.length > 0 && (
          <div className="sp-flow-detail-conns">
            <span className="sp-flow-detail-sect">{t('flow.incoming')}</span>
            {incoming.map((e, i) => {
              const source = nodeMap.get(e.from);
              return (
                <span key={i} className="sp-flow-detail-conn">
                  {'\u2190'} {source?.label ?? e.from}
                  <span className="sp-flow-detail-conn-via">{e.label.toUpperCase()}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────── */
export function FlowPreview({ data }: FlowPreviewProps): React.ReactElement {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const safeData = data ?? { nodes: [], edges: [] };
  const { nodes, edges } = safeData;
  const nodeCount = nodes.length;

  const nodeMap = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const outgoing = useMemo(() => {
    const m = new Map<string, { to: string; label: string }[]>();
    for (const e of edges) {
      const list = m.get(e.from) ?? [];
      list.push({ to: e.to, label: e.label ?? 'next' });
      m.set(e.from, list);
    }
    return m;
  }, [edges]);
  const incoming = useMemo(() => {
    const m = new Map<string, { from: string; label: string }[]>();
    for (const e of edges) {
      const list = m.get(e.to) ?? [];
      list.push({ from: e.from, label: e.label ?? 'next' });
      m.set(e.to, list);
    }
    return m;
  }, [edges]);

  const dims = useMemo(() => getDims(nodeCount), [nodeCount]);
  const positions = useMemo(
    () => layoutNodes(nodes, edges, dims),
    [nodes, edges, dims],
  );

  if (nodeCount === 0) {
    return (
      <div className="sp-flow-preview">
        <div className="sp-flow-empty-msg">
          Describe your call flow and the graph will appear here.
        </div>
      </div>
    );
  }

  const allPos = Array.from(positions.values());
  const pad = 40;
  const minX = Math.min(...allPos.map(p => p.x)) - dims.w / 2 - pad;
  const maxX = Math.max(...allPos.map(p => p.x)) + dims.w / 2 + pad;
  const minY = Math.min(...allPos.map(p => p.y)) - dims.h / 2 - pad;
  const maxY = Math.max(...allPos.map(p => p.y)) + dims.h / 2 + pad;

  const selectedNode = selectedId ? nodeMap.get(selectedId) ?? null : null;

  return (
    <div className="sp-flow-preview">
      <svg
        viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
        width="100%" height="100%"
        preserveAspectRatio="xMidYMid meet"
        onClick={() => setSelectedId(null)}
      >
        <defs>
          <marker id="sp-flow-arrow" viewBox="0 0 10 10" refX={10} refY={5}
            markerWidth={6} markerHeight={6} orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={TH.edge} />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, i) => {
          const fromPos = positions.get(edge.from);
          const toPos = positions.get(edge.to);
          if (!fromPos || !toPos) return null;
          const outs = outgoing.get(edge.from) ?? [];
          const pi = outs.findIndex(o => o.to === edge.to && o.label === (edge.label ?? 'next'));
          const from = {
            x: pX(fromPos.x, dims.w, pi >= 0 ? pi : 0, outs.length),
            y: fromPos.y + dims.h / 2,
          };
          const to = { x: toPos.x, y: toPos.y - dims.h / 2 };
          return <EdgeLine key={i} from={from} to={to} />;
        })}

        {/* Nodes */}
        {nodes.map(node => {
          const pos = positions.get(node.id);
          if (!pos) return null;
          return (
            <NodeCard key={node.id} node={node}
              cx={pos.x} cy={pos.y} dims={dims}
              outputs={outgoing.get(node.id) ?? []}
              selected={selectedId === node.id}
              onClick={() => setSelectedId(selectedId === node.id ? null : node.id)}
            />
          );
        })}
      </svg>

      {/* Click-to-inspect detail panel (overlaid) */}
      {selectedNode && (
        <div className="sp-flow-detail-overlay">
          <DetailPanel
            node={selectedNode}
            outgoing={outgoing.get(selectedNode.id) ?? []}
            incoming={incoming.get(selectedNode.id) ?? []}
            nodeMap={nodeMap}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}

      <span className="sp-flow-node-count">{nodeCount} node{nodeCount !== 1 ? 's' : ''}</span>
    </div>
  );
}
